import { ReactElement } from 'react';
import { Avatar } from '@twilio-paste/core/avatar';
import { Box } from '@twilio-paste/core/box';
import { Heading } from '@twilio-paste/core/heading';
import RouterAnchor from '@twiliointernal/services-common/components/routerAnchor';
import { SkeletonLoader } from '@twilio-paste/core/skeleton-loader';
import { Text } from '@twilio-paste/core/text';

import { getProductIcon } from './utils';
import { types, copy } from './definitions';
import styles from './productCard.module.css';
import { dataTestIds } from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

/**
 * Card component with product icon on top
 *  - Height is configurable. At the time of writing, OSC Landing page requires
 *    fixed value for height
 *  - Padding adding is set to `space60` (same as the default value of Paste's Card component)
 */
const ProductCard = ({
  articlesCount,
  children,
  heading,
  height = 'auto',
  icon,
  isLoading,
  viewMoreOptions,
}: types.ProductCardProps): ReactElement => {
  return (
    <Box
      borderColor="colorBorderWeaker"
      className={styles.container}
      data-testid={dataTestIds.productCard.default}
      height={height}
      marginTop={['space70', 'space100', 'space100']}
      _hover={{ boxShadow: 'shadow' }}
    >
      {icon && (
        <Box className={styles.iconContainer}>
          {isLoading ? (
            <SkeletonLoader
              borderRadius="borderRadiusCircle"
              size="sizeIcon110"
            />
          ) : (
            <Avatar
              name={icon.name}
              size="sizeIcon110"
              color={icon.color ? icon.color : 'decorative10'}
              icon={getProductIcon(icon.name ?? types.ProductIconName.Twilio)}
            />
          )}
        </Box>
      )}
      {heading && (
        <Heading
          as="h4"
          data-testid={dataTestIds.productCard.sectionTitle}
          variant="heading40"
          marginBottom="space0"
        >
          {heading}
        </Heading>
      )}
      {articlesCount && (
        <Text
          as="p"
          data-testid={dataTestIds.productCard.articleCount}
          fontWeight="fontWeightSemibold"
          color="colorTextWeak"
          marginBottom="space30"
        >
          {copy.articlesCount(articlesCount)}
        </Text>
      )}
      {children}
      {viewMoreOptions?.isVisible && (
        <Box marginTop="space30">
          <RouterAnchor
            className={styles.viewMore}
            noUnderline
            to={viewMoreOptions.href}
            trackEvent={viewMoreOptions.trackEvent}
          >
            {copy.viewMore}
          </RouterAnchor>
        </Box>
      )}
    </Box>
  );
};

export default ProductCard;
